/*
 * cart-rewards: shared core for the cart drawer rewards bar (cart-gwp.js) and upsells (cart-upsells.js).
 *
 * - cartRequest(): POSTs to the Cart AJAX API, tagged so the app-change observer ignores it
 * - publishCart(): re-renders the drawer through Prestige's own `cart:change` event
 * - formatMoney(), currencyRate(), announce() (aria-live), track() (analytics)
 * - App cart observer: Prestige only refreshes the drawer for its own cart requests. Apps (Tolstoy, Yotpo,
 *   Bidit, ...) call the Cart API directly, so we watch fetch/XHR for successful cart writes that did not
 *   come from the theme or from us, and fire the theme's own `cart:refresh`.
 *
 * Everything is wrapped so that a failure here can never break the theme's cart or checkout.
 */
(() => {
  if (window.CartRewards) return;

  const OWN_REQUEST_HEADER = 'X-Cart-Rewards';
  const CART_WRITE_PATH = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  const OPEN_DRAWER_ON_APP_ADD = true;

  const root = () => window.Shopify?.routes?.root || '/';

  /* ---------------------------------------------------------------- cart requests */

  async function cartRequest(action, body) {
    const response = await fetch(`${root()}cart/${action}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', [OWN_REQUEST_HEADER]: '1' },
      body: JSON.stringify(body)
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(json.description || json.message || `Cart ${action} failed (${response.status})`);
    }
    return json;
  }

  // Fetch the fresh cart and drawer HTML, then let Prestige update the drawer, cart count, etc.
  async function publishCart() {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));

    const [cart, renderedSections] = await Promise.all([
      fetch(`${root()}cart.js`).then((response) => response.json()),
      sections.length ? fetch(`${root()}?sections=${sections.join(',')}`).then((response) => response.json()) : {}
    ]);

    if (window.themeVariables?.settings?.pageType === 'cart') {
      window.location.reload();
      return;
    }

    cart.sections = renderedSections;
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent: 'cart-rewards', cart }
    }));
  }

  /* ---------------------------------------------------------------- money */

  const currencyRate = () => Number(window.Shopify?.currency?.rate) || 1;

  function formatMoney(cents) {
    const format = window.themeVariables?.settings?.moneyFormat || '${{amount}}';
    const value = Math.max(0, Math.round(Number(cents) || 0));
    const number = (decimals, thousands = ',', decimal = '.') => {
      const [whole, fraction] = (value / 100).toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };
    const formatted = format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals': return number(0);
        case 'amount_with_comma_separator': return number(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return number(0, '.', ',');
        case 'amount_with_apostrophe_separator': return number(2, "'", '.');
        case 'amount_with_space_separator': return number(2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return number(0, ' ');
        default: return number(2);
      }
    });
    return formatted.replace(/<[^>]*>/g, '');
  }

  /* ---------------------------------------------------------------- accessibility */

  // One persistent live region, kept inside the drawer (the drawer is modal, so content outside it is ignored by
  // screen readers while it is open). Re-attached after every drawer re-render.
  const liveRegion = document.createElement('p');
  liveRegion.className = 'cart-rewards-live sr-only';
  liveRegion.setAttribute('role', 'status');
  liveRegion.setAttribute('aria-live', 'polite');

  function attachLiveRegion() {
    const host = document.querySelector('cart-drawer') || document.body;
    if (liveRegion.parentNode !== host) host.append(liveRegion);
  }

  function announce(text) {
    if (!text) return;
    attachLiveRegion();
    liveRegion.textContent = '';
    setTimeout(() => { liveRegion.textContent = text; }, 100);
  }

  document.addEventListener('cart-drawer:refreshed', attachLiveRegion);

  /* ---------------------------------------------------------------- analytics */

  const api = {
    analyticsEnabled: false,
    cartRequest,
    publishCart,
    formatMoney,
    currencyRate,
    announce,
    track(name, data = {}) {
      if (!api.analyticsEnabled) return;
      try { window.Shopify?.analytics?.publish?.(name, data); } catch (error) { /* ignore */ }
      try { if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name, ...data }); } catch (error) { /* ignore */ }
    }
  };

  /* ---------------------------------------------------------------- app cart observer */

  let refreshTimer = null;
  let appAdded = false;

  function scheduleAppRefresh(url) {
    appAdded = appAdded || /\/cart\/add/.test(url);
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      const drawer = document.querySelector('cart-drawer');
      const rebuyCartActive = document.querySelector('.rebuy-cart__flyout');
      if (appAdded && OPEN_DRAWER_ON_APP_ADD && drawer && !rebuyCartActive && window.themeVariables?.settings?.cartType === 'drawer'
          && window.themeVariables?.settings?.pageType !== 'cart' && typeof drawer.show === 'function') {
        drawer.show();
      }
      appAdded = false;
    }, 300);
  }

  // Theme requests always bundle `sections`; cart note/attribute saves don't change totals.
  function isThemeOrNoteRequest(body) {
    if (!body) return false;
    if (typeof body === 'string') {
      if (body.includes('sections')) return true;
      try {
        return Object.keys(JSON.parse(body)).every((key) => key === 'note' || key === 'attributes');
      } catch (error) {
        return /(^|&)sections=/.test(body);
      }
    }
    if (body instanceof FormData || body instanceof URLSearchParams) return body.has('sections');
    return false;
  }

  function hasOwnHeader(headers) {
    if (!headers) return false;
    if (headers instanceof Headers) return headers.has(OWN_REQUEST_HEADER);
    if (Array.isArray(headers)) return headers.some(([key]) => key === OWN_REQUEST_HEADER);
    return OWN_REQUEST_HEADER in headers;
  }

  function isAppCartWrite(method, url, body, headers) {
    return String(method).toUpperCase() === 'POST' && CART_WRITE_PATH.test(url) && !hasOwnHeader(headers) && !isThemeOrNoteRequest(body);
  }

  try {
    const originalFetch = window.fetch;
    window.fetch = function (input, init) {
      const promise = originalFetch.apply(this, arguments);
      try {
        const url = typeof input === 'string' ? input : (input && input.url) || String(input);
        const method = (init && init.method) || (input && input.method) || 'GET';
        if (isAppCartWrite(method, url, init && init.body, (init && init.headers) || (input && input.headers))) {
          promise.then((response) => { if (response.ok) scheduleAppRefresh(url); }, () => {});
        }
      } catch (error) { /* never interfere with the request */ }
      return promise;
    };

    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      try { this.__cartRewards = { method, url: String(url) }; } catch (error) { /* ignore */ }
      return originalOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      try {
        const info = this.__cartRewards;
        if (info && isAppCartWrite(info.method, info.url, body)) {
          this.addEventListener('load', () => {
            if (this.status >= 200 && this.status < 300) scheduleAppRefresh(info.url);
          });
        }
      } catch (error) { /* ignore */ }
      return originalSend.apply(this, arguments);
    };
  } catch (error) {
    console.warn('[cart-rewards] app cart observer disabled', error);
  }

  window.CartRewards = api;
})();
